import React, { useCallback, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { SECTION_ITEM_PROJECTS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PROJECTS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { JiraHoldoutCustomData } from '@atlassian/holdout-utils/jira-holdout';

import { fg } from '@atlassian/jira-feature-gating';
import { useRequiredErrorAnalytics } from '../../../common/controllers/use-required-error-analytics/useRequiredErrorAnalytics.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../common/constants.tsx';
import { Nav4ProjectsContent } from '../../../common/ui/content/Nav4ProjectsContent.tsx';
import type {
	Nav4ProjectsForLanding$key,
	Nav4ProjectsForLanding$data,
} from './__generated__/Nav4ProjectsForLanding.graphql';

type Nav4ProjectsForLandingProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRef: Nav4ProjectsForLanding$key;
};

export function Nav4ProjectsForLanding(props: Nav4ProjectsForLandingProps): React.JSX.Element {
	const { queryRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PROJECTS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4ProjectsForLanding$key>(
		graphql`
			fragment Nav4ProjectsForLanding on Query @argumentDefinitions(currentURL: { type: URL }) {
				...Nav4ProjectsContent @arguments(currentURL: $currentURL)
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PROJECTS,
					sectionItem: SECTION_ITEM_PROJECTS,
				});
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ProjectsForLandingInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={MENU_ID_PROJECTS}
			queryRef={data}
		/>
	);
}

const Nav4ProjectsForLandingInner = componentWithFG(
	'navx-global-side-nav',
	Nav4ProjectsForLandingInnerGSN,
	Nav4ProjectsForLandingInnerNav4,
);

type InnerProps = Omit<Nav4ProjectsForLandingProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	queryRef: Nav4ProjectsForLanding$data;
};

function Nav4ProjectsForLandingInnerGSN(props: InnerProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		onExpandFlyoutToggle,
		queryRef,
		visualContentRef,
	} = props;

	const onError = useRequiredErrorAnalytics();

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-projects-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-projects-landing">
					<JiraHoldoutCustomData />
					<JSErrorBoundary
						id="nav4-sidebar-projects-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
						{...(fg('add_analytics_track_event_for_required_error') ? { onError } : {})}
					>
						{/* This component uses the fragment from the ancestor query. */}
						<Nav4ProjectsContent queryRef={queryRef} />
					</JSErrorBoundary>
				</UFOSegment>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4ProjectsForLandingInnerNav4(props: InnerProps) {
	const {
		actionsOnHover,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		queryRef,
	} = props;

	const onError = useRequiredErrorAnalytics();

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				// Prevent double-up of accessible name that occurs in icon + text situation.
				aria-label={formattedMessage}
				elemBefore={icon}
				testId={getTestId(menuId)}
				interactionName="nav4-sidebar-projects-landing"
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-projects-landing">
					<JiraHoldoutCustomData />
					<JSErrorBoundary
						id="nav4-sidebar-projects-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
						{...(fg('add_analytics_track_event_for_required_error') ? { onError } : {})}
					>
						{/* This component uses the fragment from the ancestor query. */}
						<Nav4ProjectsContent queryRef={queryRef} />
					</JSErrorBoundary>
				</UFOSegment>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
